import React, { useState, useEffect } from 'react';
import Modal from '../modals/Modal';
import { Input } from '../forms/FormComponents';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { authService } from '../../services/auth';
import { LoginModalProps, LoginCredentials, User } from '../../types/auth';
import { useErrorHandler, useValidationHandler, ValidationRules } from '../../hooks';

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { error, clearError, handleAsyncError } = useErrorHandler();
  const { fieldErrors, validateField, clearAllFieldErrors } = useValidationHandler();

  useEffect(() => {
    if (isOpen) {
      setPhoneNumber('');
      setPassword('');
      clearError();
      clearAllFieldErrors();
    }
  }, [isOpen, clearError, clearAllFieldErrors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate fields
    const isPhoneValid = validateField('phoneNumber', phoneNumber, [
      ValidationRules.required('Telefon numarası'),
      ValidationRules.phone(),
    ]);

    const isPasswordValid = validateField('password', password, [
      ValidationRules.required('Şifre'),
      ValidationRules.minLength(4),
    ]);

    if (!isPhoneValid || !isPasswordValid) {
      return;
    }

    setIsLoading(true);

    const result = await handleAsyncError(async () => {
      const credentials: LoginCredentials = {
        phone_number: phoneNumber,
        password: password,
      };
      const user: User = await authService.login(credentials);
      return user;
    }, 'Kullanıcı girişi');

    if (result) {
      onLoginSuccess(result);
      onClose();
    }

    setIsLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Giriş Yap">
      <form onSubmit={handleSubmit} className="space-y-4">
        <ErrorDisplay error={error} onDismiss={clearError} />

        <div>
          <Input
            label="Telefon Numarası"
            type="tel"
            value={phoneNumber}
            onChange={setPhoneNumber}
            placeholder="5XX XXX XX XX"
            required
          />
          {fieldErrors.phoneNumber && (
            <div className="text-red-600 text-xs mt-1">{fieldErrors.phoneNumber}</div>
          )}
        </div>

        <div>
          <Input label="Şifre" type="password" value={password} onChange={setPassword} required />
          {fieldErrors.password && (
            <div className="text-red-600 text-xs mt-1">{fieldErrors.password}</div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition disabled:bg-blue-400"
          >
            {isLoading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default LoginModal;
